// /g/{id}: a group's shares (RFC-044 "Groups") — the owner's every one, a
// member's those they take part in — as /shares' rows (byj-1wv.51.5): live
// ones first, then the rest, each opening its own page, its menu the one
// /shares has, and no link back to the group it is shown in. The list is
// read and kept current as /shares' is (shareList.ts), but always, not only
// while one is live: shares join a group from other people's machines.
import { useCallback } from "react";
import { api, type Group as GroupT, type ShareSummary } from "../api";
import { useConfirm } from "../components/Confirm";
import { useEditTitle } from "../components/EditTitle";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { Grouped, liveThenEarlier } from "../components/Rows";
import { shareActions, shareMenu } from "../components/ShareActions";
import { ShareRow } from "../components/ShareRow";
import { t } from "../i18n";
import { liveThenNewest } from "../kinds";
import { useShareList } from "../shareList";

/** How often a group is read again while its page is shown. */
export const groupPollMs = 15_000;

const itsShares = (g: GroupT) => g.shares;
const withShares = (g: GroupT, shares: ShareSummary[]) => ({ ...g, shares });

export function Group({ id }: { id: string }) {
  const read = useCallback(() => api.group(id), [id]);
  const { data: g, error, load, gone, renamed, fail } = useShareList(read, groupPollMs, itsShares, withShares, true);
  const [confirm, confirmDialog] = useConfirm();
  const [editTitle, editDialog] = useEditTitle();

  // a group never read is the error in the page's place, with Retry; once it
  // is shown, what a poll, a retry or a row's menu could not do is said over
  // its rows
  if (!g)
    return error ? (
      <PageNotice error={error}>
        <button onClick={() => void load()}>{t("common:retry")}</button>
      </PageNotice>
    ) : (
      <PageLoading />
    );
  const actions = shareActions({ confirm, editTitle, renamed, gone, fail });
  const live = g.shares.filter((s) => s.live).length;
  return (
    <Page>
      {confirmDialog}
      {editDialog}
      <PageHead title={g.title} meta={<span className="muted">{t("shares:groupOf", { owner: g.owner.name, count: g.shares.length, live })}</span>} />
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void load()}>{t("common:retry")}</button>
        </p>
      )}
      {g.shares.length === 0 ? (
        <p className="muted">{t("shares:groupEmpty")}</p>
      ) : (
        // one container for both lists, as /shares' tab panel is: a row's meta
        // goes under its title by the list's width, and an open ⋯ menu is
        // drawn over the list after it
        <div className="rows-panel">
          <Grouped
            id="group"
            groups={liveThenEarlier(g.shares.slice().sort(liveThenNewest))}
            row={(s) => <ShareRow key={s.id} share={s} menu={shareMenu(s, actions)} groupLink={false} />}
          />
        </div>
      )}
    </Page>
  );
}
